import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FileUp, Image as ImageIcon, ImageDown, Layers, Minimize2, RotateCw, ScanText, Scissors, Stamp, X, Download, ArrowRight, Loader2, EyeOff, GripVertical, FileText, Lock, LockOpen, Eraser, FilePen, Receipt, PenLine, CheckCircle2 } from "lucide-react";
import { EditTools } from "@/components/engines/EditTools";
import { InvoiceForm } from "@/components/engines/InvoiceForm";
import { fmt, SizeTicker } from "@/components/engines/shared";
import { ThumbnailPanel } from "@/components/engines/ThumbnailPanel";
import { PagesPanel } from "@/components/engines/PagesPanel";
import { CompressPanel } from "@/components/engines/CompressPanel";
import { Pdf2ImgPanel } from "@/components/engines/Pdf2ImgPanel";
import { WatermarkPanel } from "@/components/engines/WatermarkPanel";
import { PasswordPanel } from "@/components/engines/PasswordPanel";
import { RotatePanel } from "@/components/engines/RotatePanel";
import { RedactPanel } from "@/components/engines/RedactPanel";
import { SignPanel } from "@/components/engines/SignPanel";
import { FeedbackPrompt } from "@/components/engines/FeedbackPrompt";
import { savePending } from "@/utils/pendingFile";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIONS = [
  { id: "compress", label: "Compress", icon: Minimize2 },
  { id: "merge", label: "Merge", icon: Layers },
  { id: "img2pdf", label: "Img→PDF", icon: ImageIcon },
  { id: "extract", label: "Text", icon: ScanText },
  { id: "pages", label: "Pages", icon: Scissors },
  { id: "rotate", label: "Rotate", icon: RotateCw },
  { id: "pdf2img", label: "To PNG", icon: ImageDown },
  { id: "pdf2docx", label: "To DOCX", icon: FileText },
  { id: "watermark", label: "Stamp", icon: Stamp },
  { id: "redact", label: "Redact", icon: EyeOff },
  { id: "protect", label: "Protect", icon: Lock },
  { id: "unlock", label: "Unlock", icon: LockOpen },
  { id: "scrub", label: "Scrub", icon: Eraser },
  { id: "edit", label: "Edit", icon: FilePen },
  { id: "invoice", label: "Invoice", icon: Receipt },
  { id: "sign", label: "Sign", icon: PenLine },
];

export const PdfTool = ({ token = null, onComplete = null, maxMb = 50, locked = false, light = false, guest = false, initialFiles = null, initialAction = null, initialParams = null } = {}) => {
  const activePicker = light ? "bg-ink text-white border border-ink" : "bg-white/10 text-white border border-white/10";
  const [guestToken] = useState(() => {
    let t = localStorage.getItem("doclyx_guest_token");
    if (!t) {
      t = crypto.randomUUID();
      localStorage.setItem("doclyx_guest_token", t);
    }
    return t;
  });
  const [guestLocked, setGuestLocked] = useState(false);
  const [guestDone, setGuestDone] = useState(false);
  const [files, setFiles] = useState([]);
  const [action, setAction] = useState(initialAction || "compress");
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [pageRange, setPageRange] = useState("");
  const [angle, setAngle] = useState(90);
  const [targetKb, setTargetKb] = useState(initialParams?.targetKb || 0);
  const [imgFormat, setImgFormat] = useState(initialParams?.outFormat === "jpg" ? "jpg" : "png");
  const [watermarkText, setWatermarkText] = useState("");
  const [password, setPassword] = useState("");
  const [editSpec, setEditSpec] = useState({ texts: [], rotate: {}, delete: [] });
  const [thumbs, setThumbs] = useState([]);
  const [thumbLoading, setThumbLoading] = useState(false);
  const [thumbError, setThumbError] = useState(null);
  const [thumbMeta, setThumbMeta] = useState({ totalPages: 0, truncated: false });
  const [redactMode, setRedactMode] = useState("draw");
  const [redactText, setRedactText] = useState("");
  const [redactPages, setRedactPages] = useState("");
  const [redactions, setRedactions] = useState([]);
  const [pageOrder, setPageOrder] = useState([]);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [sigData, setSigData] = useState(null);
  const [sigFields, setSigFields] = useState([]);

  const appliedInitial = useRef(false);
  useEffect(() => {
    if (appliedInitial.current || !initialFiles?.length) return;
    appliedInitial.current = true;
    const act = initialAction || "compress";
    setFiles(initialFiles.slice(0, 20));
    setAction(act);
    if (act !== "img2pdf") loadThumbs(initialFiles[0], act);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiles, initialAction]);

  useEffect(() => {
    if (!guest) return;
    axios.get(`${API}/pdf/guest-status`, { params: { token: guestToken } })
      .then((res) => { if (res.data.used) setGuestLocked(true); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest]);

  const IMAGE_RE = /\.(jpe?g|png|webp)$/i;
  const hasImage = files.some((f) => f.type.startsWith("image/") || IMAGE_RE.test(f.name));
  const hasPdf = files.some((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
  const visibleActions = useMemo(() => {
    if (!files.length) return ACTIONS;
    if (hasImage && !hasPdf) return ACTIONS.filter((a) => a.id === "img2pdf");
    if (hasImage && hasPdf) return ACTIONS;
    return ACTIONS.filter((a) => a.id !== "img2pdf");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const conformAction = useCallback((nextFiles) => {
    const img = nextFiles.some((f) => f.type.startsWith("image/") || IMAGE_RE.test(f.name));
    const pdf = nextFiles.some((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (img && !pdf && action !== "img2pdf") setAction("img2pdf");
    else if (!img && pdf && action === "img2pdf") setAction("compress");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  useEffect(() => {
    const nums = thumbs.map((p) => p.page);
    setPageOrder(nums);
    setSelectedPages(new Set(nums));
  }, [thumbs]);

  const dragFileIndex = useRef(null);
  const inputRef = useRef(null);

  const clearThumbs = useCallback(() => {
    setThumbs([]);
    setThumbMeta({ totalPages: 0, truncated: false });
    setThumbError(null);
    setThumbLoading(false);
    setRedactions([]);
  }, []);

  const loadThumbs = useCallback(async (file, currentAction = action) => {
    if (!file || currentAction === "img2pdf") {
      clearThumbs();
      return;
    }
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      clearThumbs();
      return;
    }
    setThumbLoading(true);
    setThumbError(null);
    setRedactions([]);
    const fd = new FormData();
    fd.append("file", file);
    if (guest) fd.append("guest", guestToken);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/pdf/thumbnails`, fd, { headers });
      setThumbs(res.data.pages || []);
      setThumbMeta({ totalPages: res.data.total_pages || 0, truncated: !!res.data.truncated });
    } catch {
      setThumbs([]);
      setThumbMeta({ totalPages: 0, truncated: false });
      setThumbError("unavailable");
    } finally {
      setThumbLoading(false);
    }
  }, [action, token, clearThumbs, guest, guestToken]);

  const addFiles = useCallback((list) => {
    const incoming = Array.from(list);
    const base = guest && guestDone ? [] : files;
    const next = [...base, ...incoming].slice(0, 20);
    setFiles(next);
    setResult(null);
    setError(null);
    setStatus("idle");
    setRedactions([]);
    conformAction(next);
    if (next[0]) loadThumbs(next[0]);
    else clearThumbs();
  }, [files, loadThumbs, clearThumbs, conformAction, guest, guestDone]);

  const removeFile = (index) => {
    const next = files.filter((_, i) => i !== index);
    setFiles(next);
    setResult(null);
    setStatus("idle");
    conformAction(next);
    if (next[0]) loadThumbs(next[0]);
    else clearThumbs();
  };

  const switchAction = (id) => {
    const wasImages = action === "img2pdf";
    const willImages = id === "img2pdf";
    setAction(id);
    setResult(null);
    setStatus("idle");
    setPageRange("");
    setAngle(90);
    setWatermarkText("");
    setPassword("");
    setEditSpec({ texts: [], rotate: {}, delete: [] });
    setRedactMode("draw");
    setRedactText("");
    setRedactPages("");
    setRedactions([]);
    setSigData(null);
    setSigFields([]);
    if (wasImages !== willImages) {
      setFiles([]);
      clearThumbs();
      if (inputRef.current) inputRef.current.value = "";
    } else if (!willImages && files[0] && !thumbs.length && !thumbLoading) {
      loadThumbs(files[0], id);
    }
  };

  const addRedaction = (page, rect) => setRedactions((prev) => [...prev, { page, ...rect }].slice(0, 200));
  const removeRedaction = (page, index) => {
    setRedactions((prev) => {
      const pageRects = prev.filter((r) => r.page === page);
      const target = pageRects[index];
      return prev.filter((r) => r !== target);
    });
  };

  const addSigField = (page, rect) => setSigFields((prev) => [...prev, { page, ...rect }].slice(0, 20));
  const removeSigField = (page, index) => {
    setSigFields((prev) => {
      const pageRects = prev.filter((r) => r.page === page);
      const target = pageRects[index];
      return prev.filter((r) => r !== target);
    });
  };

  const reorderPages = (source, target) => {
    if (!source || !target || source === target) return;
    setPageOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(source);
      const to = next.indexOf(target);
      if (from < 0 || to < 0) return prev;
      next.splice(from, 1);
      next.splice(to, 0, source);
      return next;
    });
  };

  const togglePage = (page) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      if (next.has(page)) next.delete(page);
      else next.add(page);
      return next;
    });
  };

  const movePage = (page, delta) => {
    setPageOrder((prev) => {
      const index = prev.indexOf(page);
      const target = index + delta;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const moveFile = (from, to) => {
    if (from === null || from === to) return;
    const next = [...files];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setFiles(next);
    setResult(null);
    if (next[0]) loadThumbs(next[0], action);
  };

  const pageOverride = pageRange.trim();
  const visualPagesReady = action === "pages" && pageOrder.length > 0;
  const redactionMissing = action === "redact" && (redactMode === "draw" ? redactions.length === 0 : !redactText.trim());
  const editMissing = action === "edit" && editSpec.texts.length === 0 && Object.keys(editSpec.rotate).length === 0 && editSpec.delete.length === 0;
  const signMissing = action === "sign" && (!sigData || sigFields.length === 0);
  const needsParam =
    (action === "pages" && (pageOverride ? false : visualPagesReady ? selectedPages.size === 0 : true)) ||
    (action === "watermark" && !watermarkText.trim()) ||
    (action === "protect" && password.trim().length < 4) ||
    (action === "unlock" && !password.trim()) ||
    editMissing ||
    signMissing ||
    redactionMissing;

  const process = async () => {
    if (!files.length || status === "processing") return;
    if (needsParam) return;
    setStatus("processing");
    setError(null);
    const fd = new FormData();
    fd.append("action", action);
    files.forEach((f) => fd.append("files", f));
    if (action === "pages") {
      const visualSelection = pageOrder.filter((p) => selectedPages.has(p)).join(",");
      fd.append("pages", pageOverride || visualSelection);
    }
    if (action === "rotate") fd.append("angle", String(angle));
    if (action === "compress" && targetKb) fd.append("target_kb", String(targetKb));
    if (action === "pdf2img") fd.append("out_format", imgFormat);
    if (action === "watermark") fd.append("text", watermarkText.trim());
    if (action === "protect" || action === "unlock") fd.append("password", password.trim());
    if (action === "edit") fd.append("edits", JSON.stringify(editSpec));
    if (action === "sign") {
      fd.append("signature", sigData.b64);
      fd.append("sig_name", sigData.name);
      fd.append("sig_fields", JSON.stringify(sigFields.map((r) => ({
        page: r.page,
        x: r.x0,
        y: r.y0,
        w: r.x1 - r.x0,
        h: r.y1 - r.y0,
      }))));
    }
    if (action === "redact" && redactMode === "text") {
      fd.append("text", redactText.trim());
      if (redactPages.trim()) fd.append("pages", redactPages.trim());
    }
    if (action === "redact" && redactMode === "draw") {
      const boxes = redactions.map((r) => {
        const meta = thumbs.find((p) => p.page === r.page);
        if (!meta) return null;
        return {
          page: r.page,
          rect: [r.x0 * meta.width, r.y0 * meta.height, r.x1 * meta.width, r.y1 * meta.height],
        };
      }).filter(Boolean);
      if (!boxes.length) {
        setError("Page preview expired — re-upload the PDF and draw the blackouts again.");
        setStatus("error");
        return;
      }
      fd.append("redactions", JSON.stringify(boxes));
    }
    if (guest) fd.append("guest", guestToken);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      if (action === "extract") {
        const res = await axios.post(`${API}/pdf/process`, fd, { headers, withCredentials: true });
        setResult({ kind: "text", ...res.data });
      } else {
        const res = await axios.post(`${API}/pdf/process`, fd, { headers, withCredentials: true, responseType: "blob" });
        const before = files.reduce((s, f) => s + f.size, 0);
        const url = URL.createObjectURL(res.data);
        const cd = res.headers["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/);
        setResult({ kind: "file", url, filename: m ? m[1] : "doclyx_output.pdf", before, after: res.data.size });
      }
      setStatus("done");
      if (guest) setGuestDone(true);
      if (onComplete) onComplete();
    } catch (e) {
      if (e.response?.status === 402) {
        savePending(files, action);
        setGuestLocked(true);
        setStatus("idle");
        return;
      }
      let msg = "Processing failed. Check your files and try again.";
      if (e.response?.data instanceof Blob) {
        try {
          msg = JSON.parse(await e.response.data.text()).detail || msg;
        } catch {}
      } else if (e.response?.data?.detail) {
        const d = e.response.data.detail;
        msg = typeof d === "string" ? d : msg;
      }
      setError(msg);
      setStatus("error");
    }
  };

  const accept = action === "img2pdf" ? "image/*" : "application/pdf";

  return (
    <>
    <div className="relative overflow-hidden bg-white border border-edge rounded-sm shadow-[0_4px_40px_-10px_rgba(0,0,0,0.06)]" data-testid="pdf-tool">
      <div className="flex items-center justify-between px-6 pt-5 pb-4">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-fog">{locked ? "Doclyx — Workspace preview" : guest ? "Doclyx — First run free" : "Doclyx — Document workspace"}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">Files never stored</span>
      </div>

      {action !== "invoice" && (
      <div
        data-testid="hero-dropzone"
        role="button"
        tabIndex={0}
        aria-label={action === "img2pdf" ? "Upload images to convert to PDF" : "Upload PDF documents"}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        className={`mx-5 border border-dashed ${dragging ? "border-azure bg-azure/[0.03]" : "border-edge bg-page hover:border-azure/50"} cursor-pointer rounded-lg transition-colors duration-300 px-6 py-12 flex flex-col items-center text-center`}
      >
        <input ref={inputRef} type="file" multiple accept={accept} className="hidden" data-testid="dropzone-file-input"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <FileUp className="w-10 h-10 text-ink mb-4" strokeWidth={1.25} />
        <p className="text-lg font-medium tracking-tight mb-1 text-ink">Drop files to begin</p>
        <p className="text-sm text-fog">
          {action === "img2pdf" ? "JPG / PNG / WEBP accepted" : "PDF documents accepted"} · Max {maxMb}MB · Files never stored
        </p>
      </div>
      )}

      <AnimatePresence>
        {files.length > 0 && action !== "invoice" && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="px-5 pt-4 overflow-hidden" data-testid="file-list">
            <div className="border border-hairline rounded-lg divide-y divide-hairline max-h-36 overflow-y-auto">
              {files.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  draggable={action === "merge"}
                  onDragStart={() => { dragFileIndex.current = i; }}
                  onDragOver={(e) => { if (action === "merge") e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); moveFile(dragFileIndex.current, i); dragFileIndex.current = null; }}
                  className={`flex items-center justify-between px-4 py-2.5 ${action === "merge" ? "cursor-grab active:cursor-grabbing" : ""}`}
                  data-testid={`file-item-${i}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {action === "merge" && <GripVertical className="w-4 h-4 text-mist shrink-0" strokeWidth={1.5} data-testid={`file-drag-${i}`} />}
                    <span className="font-mono text-xs text-ink truncate max-w-[240px]">{f.name}</span>
                    {action === "merge" && <span className="font-mono text-[9px] text-mist shrink-0">ORDER {i + 1}</span>}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-mist">{fmt(f.size)}</span>
                    <button data-testid={`file-remove-${i}`} onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-mist hover:text-ink hover:bg-black/5 transition-colors duration-300"><X className="w-4 h-4" strokeWidth={1.5} /></button>
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mx-5 mt-4 grid grid-cols-2 sm:grid-cols-5 gap-1 p-1 bg-page border border-hairline rounded-sm" data-testid="action-picker">
        {visibleActions.map((a) => (
          <button key={a.id} data-testid={`action-${a.id}`} onClick={() => switchAction(a.id)}
            className={`py-2 px-1 min-h-[44px] rounded-full text-[10px] font-medium tracking-wide flex flex-col items-center justify-center gap-1 transition-colors duration-300 ${action === a.id ? activePicker : "text-fog hover:text-ink"}`}>
            <a.icon className="w-4 h-4" strokeWidth={1.5} />
            {a.label}
          </button>
        ))}
      </div>
      {files.length > 0 && visibleActions.length < ACTIONS.length && (
        <p className="mx-5 mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-mist" data-testid="engine-filter-hint">
          Showing engines that fit your file{hasImage && !hasPdf ? " — images convert via Images → PDF" : ""}
        </p>
      )}

      <AnimatePresence>
        {action === "pages" && (
          <PagesPanel
            thumbs={thumbs}
            pageOrder={pageOrder}
            selectedPages={selectedPages}
            onToggle={togglePage}
            onReorder={reorderPages}
            onMove={movePage}
            pageRange={pageRange}
            onPageRange={setPageRange}
          />
        )}
        {action === "compress" && (
          <CompressPanel targetKb={targetKb} onChange={setTargetKb} activePicker={activePicker} />
        )}
        {action === "pdf2img" && (
          <Pdf2ImgPanel imgFormat={imgFormat} onChange={setImgFormat} activePicker={activePicker} />
        )}
        {action === "watermark" && (
          <WatermarkPanel value={watermarkText} onChange={setWatermarkText} />
        )}
        {(action === "protect" || action === "unlock") && (
          <PasswordPanel action={action} value={password} onChange={setPassword} />
        )}
        {action === "rotate" && (
          <RotatePanel angle={angle} onChange={setAngle} activePicker={activePicker} />
        )}
        {action === "redact" && (
          <RedactPanel
            redactMode={redactMode}
            onModeChange={setRedactMode}
            redactText={redactText}
            onTextChange={setRedactText}
            redactPages={redactPages}
            onPagesChange={setRedactPages}
            redactionCount={redactions.length}
            onClear={() => setRedactions([])}
            activePicker={activePicker}
          />
        )}
        {action === "sign" && (
          <SignPanel
            sigData={sigData}
            onCapture={setSigData}
            onClearSig={() => setSigData(null)}
            fieldCount={sigFields.length}
            onClearFields={() => setSigFields([])}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {files.length > 0 && action !== "img2pdf" && action !== "pages" && action !== "edit" && action !== "invoice" && (
          <ThumbnailPanel
            loading={thumbLoading}
            error={thumbError}
            thumbs={thumbs}
            totalPages={thumbMeta.totalPages}
            truncated={thumbMeta.truncated}
            editable={(action === "redact" && redactMode === "draw") || action === "sign"}
            redactions={action === "sign" ? sigFields : redactions}
            onAdd={action === "sign" ? addSigField : addRedaction}
            onRemove={action === "sign" ? removeSigField : removeRedaction}
            fileCount={files.length}
            rectClassName={action === "sign" ? "absolute border-2 border-dashed border-azure bg-azure/10 hover:border-[#FF3333] hover:bg-[#FF3333]/10 flex items-center justify-center transition-colors duration-200" : null}
            rectLabel={action === "sign" ? "Signature" : ""}
            editableLabel={action === "sign" ? "Draw signature boxes" : "Draw permanent blackouts"}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {files.length > 0 && action === "edit" && (
          <EditTools
            thumbs={thumbs}
            loading={thumbLoading}
            totalPages={thumbMeta.totalPages}
            truncated={thumbMeta.truncated}
            spec={editSpec}
            onSpecChange={setEditSpec}
            fileCount={files.length}
          />
        )}
        {action === "invoice" && (
          <InvoiceForm
            token={token}
            onComplete={onComplete}
            guest={guest}
            guestToken={guestToken}
            guestDone={guestDone}
            onGuestDone={() => setGuestDone(true)}
            onGuestLocked={() => setGuestLocked(true)}
          />
        )}
      </AnimatePresence>

      {action === "redact" && redactMode === "draw" && redactions.length > 0 && (
        <p className="mx-5 mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-azure" data-testid="redaction-count">
          {redactions.length} blackout{redactions.length === 1 ? "" : "s"} ready
        </p>
      )}

      {action === "sign" && sigFields.length > 0 && (
        <p className="mx-5 mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-azure" data-testid="sign-field-count">
          {sigFields.length} signature box{sigFields.length === 1 ? "" : "es"} placed
        </p>
      )}

      {action !== "invoice" && (
      <div className="p-5 pb-3">
        <button data-testid="process-button" onClick={process} disabled={!files.length || status === "processing" || needsParam}
          className="w-full h-11 rounded-full v4-btn-primary text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300">
          {status === "processing" ? (<><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Processing…</>) : (<>Run <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>)}
        </button>
        {status === "processing" && (
          <div className="mt-3" data-testid="process-progress">
            <div className="h-1 rounded-full bg-black/10 overflow-hidden">
              <motion.div
                className="h-full w-1/3 bg-azure rounded-full"
                animate={{ x: ["-110%", "320%"] }}
                transition={{ repeat: Infinity, duration: 1.1, ease: "easeInOut" }}
              />
            </div>
            <p className="font-mono text-[10px] text-mist mt-2 text-center tabular-nums">
              Working on {fmt(files.reduce((s, f) => s + f.size, 0))}…
            </p>
          </div>
        )}
      </div>
      )}

      <AnimatePresence>
        {status === "done" && result && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mx-5 mb-5 border border-hairline rounded-lg p-4" data-testid="process-result">
            {result.kind === "file" ? (
              <div>
                <div className="flex items-center gap-2 mb-1.5" data-testid="process-done-check">
                  <CheckCircle2 className="w-4 h-4 text-azure" strokeWidth={2} />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink">Done</span>
                </div>
                <p className="font-mono text-xs text-fog mb-3">
                  {fmt(result.before)} → <SizeTicker from={result.before} to={result.after} />
                  {action === "compress" && result.after < result.before && Math.round((1 - result.after / result.before) * 100) >= 1 && (
                    <span className="text-azure"> · −{Math.round((1 - result.after / result.before) * 100)}%</span>
                  )}
                  {action === "compress" && !(result.after < result.before && Math.round((1 - result.after / result.before) * 100) >= 1) && (
                    <span className="text-azure"> · already optimized</span>
                  )}
                  {action === "redact" && <span className="text-azure"> · permanently redacted</span>}
                  {action === "sign" && <span className="text-azure"> · signed & dated</span>}
                </p>
                <a href={result.url} download={result.filename} data-testid="download-button"
                  className="flex items-center justify-center gap-2 v4-btn-primary h-11 px-4 rounded-full text-sm font-semibold transition-colors duration-300">
                  <Download className="w-4 h-4" strokeWidth={1.5} /> Download {result.filename}
                </a>
                <FeedbackPrompt />
                {guest && guestDone && (
                  <div className="mt-3 pt-3 border-t border-hairline flex items-center justify-between gap-3 flex-wrap" data-testid="guest-unlock-cta">
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-fog">That one was free — unlimited runs live in the workspace</p>
                    <a
                      href="#pricing"
                      onClick={() => { savePending(files, action); trackEvent("cta_click", { source: "guest_unlock" }); }}
                      className="font-mono text-[11px] uppercase tracking-[0.15em] text-azure hover:text-ink transition-colors duration-300"
                    >
                      Unlock workspace →
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-2">{result.pages} pages · {result.characters} chars extracted</p>
                <pre className="font-mono text-[11px] leading-relaxed text-white/70 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md p-3 bg-ink" data-testid="extracted-text">
                  {result.text || "(no extractable text found)"}
                </pre>
              </div>
            )}
          </motion.div>
        )}
        {status === "error" && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mx-5 mb-5 border border-[#FF3333]/30 text-[#FF3333] rounded-lg px-4 py-3 text-xs font-medium" data-testid="process-error">
            Error — {error}
          </motion.p>
        )}
      </AnimatePresence>

      {(locked || guestLocked) && (
        <div
          data-testid="tool-lock-overlay"
          onClick={() => {
            trackEvent("cta_click", { source: "locked_tool" });
            document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
          }}
          className={`absolute inset-0 z-10 overflow-y-auto cursor-pointer ${light ? "bg-black/40" : "bg-black/35"}`}
        >
          <div className="min-h-full flex items-center justify-center p-6">
          <div className={`max-w-sm text-center rounded-sm border p-6 md:p-8 ${
            light
              ? "bg-white border-edge shadow-[0_24px_60px_-24px_rgba(17,17,16,0.35)]"
              : "bg-black/80 border-white/10 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.7)]"
          }`}>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-bronze block mb-4">{guestLocked ? "Free conversion used" : "Member workspace"}</span>
            <h3 className={`font-medium tracking-tight text-2xl mb-3 ${light ? "text-ink" : "text-white"}`}>
              {guestLocked ? "You saw it work. Keep going with a plan." : "Choose a plan to unlock the live engines."}
            </h3>
            <p className={`text-sm leading-relaxed mb-7 ${light ? "text-fog" : "text-zinc-300"}`}>
              {guestLocked
                ? "Your file is saved and waiting — unlock the workspace and you'll land right back on it, mid-task. Plans start at $5/mo."
                : "Compress, merge, redact and convert client-ready files inside your private workspace. Plans start at $5/mo."}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <a href="#pricing" data-testid="tool-lock-cta" onClick={(e) => { e.stopPropagation(); if (guestLocked) savePending(files, action); trackEvent("cta_click", { source: "locked_tool_view_plans" }); }}
                className="inline-flex items-center justify-center gap-2 v4-btn-primary min-h-[44px] px-5 rounded-full text-sm font-semibold transition-colors duration-300">
                View plans <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a href="#pricing" data-testid="tool-lock-trial" onClick={(e) => { e.stopPropagation(); if (guestLocked) savePending(files, action); trackEvent("cta_click", { source: "locked_tool_trial" }); }}
                className={`inline-flex items-center justify-center border min-h-[44px] px-5 rounded-full text-sm font-medium transition-colors duration-300 ${
                  light ? "border-edge text-ink hover:border-ink" : "border-white/20 text-white hover:border-white/50"
                }`}>
                14-day trial
              </a>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
    {guest && (
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mt-4 text-right" data-testid="guest-microcopy">
        {guestLocked ? "Free conversion used — plans from $5/mo" : "First conversion free — no account needed"}
      </p>
    )}
    </>
  );
};
